import type { FetchBaseQueryError } from "@reduxjs/toolkit/query";

/**
 * Type guard for an RTK Query `FetchBaseQueryError` that carries a **numeric**
 * HTTP status (i.e. the server responded, as opposed to a network / timeout /
 * parse failure whose `status` is a string literal, or a `SerializedError`
 * which has no `status` at all).
 *
 * Shared so the several call sites that branch on a specific upstream status
 * (`resolveBeneficiaryErrorVariant`, `verifyBeneficiaryTemplates`, …) don't each
 * re-implement the same shape check.
 */
export function isFetchBaseQueryError(
  error: unknown
): error is FetchBaseQueryError {
  return (
    typeof error === "object" &&
    error !== null &&
    "status" in error &&
    typeof (error as { status: unknown }).status === "number"
  );
}

/**
 * The numeric HTTP status of an RTK Query error, or `undefined` when there
 * isn't one — network failure (`FETCH_ERROR`), client timeout (`TIMEOUT_ERROR`),
 * parse failure (`PARSING_ERROR`), a custom error, or a `SerializedError`.
 */
export function getFetchErrorStatus(error: unknown): number | undefined {
  return isFetchBaseQueryError(error) ? (error.status as number) : undefined;
}
